import ApplicationDetail from 'components/global/ApplicationDetail/ApplicationDetail';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchApplication } from 'components/global/ApplicationDetail/ApplicationDetail.helpers';
import { useLocation } from 'react-router-dom';
import BreadCrumbWrapper from '../../../global/BreadCrumbs/BreadCrumbs';
import applicationDetailSignal from 'components/global/ApplicationDetail/ApplicationDetail.signal';
import SendTechnicalAssistanceModal from '../EdoTechnicalAssistance/components/SendTechnicalAssistanceModal';
import { checkEdoProgramPermissions } from '../../../../libs/edoPermissions';
import loaderSignal from '../../../../signals/Loader.signal';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import { getStatusStyles } from 'libs/functions/global.functions';
import $appSettings from 'signals/AppSettings.signal';
import capitalizeFirst from 'utils/capitalizeFirst';

const EdoPortfolioDetail = () => {
  const { pathname } = useLocation();
  const segments = pathname.split('/');
  const applicationId = segments[segments.length - 1];
  const { lender, business, fundingProgram, status } = applicationDetailSignal.value;
  const [canView, setCanView] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const { APPLICATION_STATUS } = $appSettings.value.constants;

  const getStatusLabel = () => {
    if (!status) return null;
    if (status === APPLICATION_STATUS.inProgress) {
      return 'Draft';
    }
    return capitalizeFirst(status.replace(/_/g, ' '));
  };

  const statusLabel = getStatusLabel();
  const statusClassName = status ? getStatusStyles(status).class : '';

  let labels = [];
  if (pathname.includes('credit-program')) {
    labels = [
      '',
      !!fundingProgram ? fundingProgram.name : '',
      !!lender && !!business ? (
        <span className="d-inline-flex align-items-center">
          <span>{`${lender.name} - ${business.name}`}</span>
          {statusLabel && (
            <span
              className={`ml-2 badge ${statusClassName}`}
              style={{ fontSize: '0.72rem', padding: '3px 8px' }}
            >
              {statusLabel}
            </span>
          )}
        </span>
      ) : (
        <span className="d-inline-flex align-items-center">
          <span>{`Application ${applicationId}`}</span>
          {statusLabel && (
            <span
              className={`ml-2 badge ${statusClassName}`}
              style={{ fontSize: '0.72rem', padding: '3px 8px' }}
            >
              {statusLabel}
            </span>
          )}
        </span>
      ),
    ];
  } else {
    labels = [
      '',
      !!lender && !!business ? (
        <span className="d-inline-flex align-items-center">
          <span>{`${lender.name} - ${business.name}`}</span>
          {statusLabel && (
            <span
              className={`ml-2 badge ${statusClassName}`}
              style={{ fontSize: '0.72rem', padding: '3px 8px' }}
            >
              {statusLabel}
            </span>
          )}
        </span>
      ) : (
        <span className="d-inline-flex align-items-center">
          <span>{`Application ${applicationId}`}</span>
          {statusLabel && (
            <span
              className={`ml-2 badge ${statusClassName}`}
              style={{ fontSize: '0.72rem', padding: '3px 8px' }}
            >
              {statusLabel}
            </span>
          )}
        </span>
      ),
    ];
  }

  // Check permissions when funding program is available (runs once per program)
  const lastCheckedRef = useRef({ programId: null, userId: null });
  useEffect(() => {
    const programId = fundingProgram?.id;
    const userId = userAccountSignal.value.userData?.id;
    if (!programId) return;
    if (
      lastCheckedRef.current.programId === programId &&
      lastCheckedRef.current.userId === userId
    ) {
      return;
    }
    lastCheckedRef.current = { programId, userId };

    const checkPermissions = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const permissions = await checkEdoProgramPermissions(
          fundingProgram.id,
          ['canView', 'canUpdate', 'canDelete']
        );
        setCanView(permissions.canView);
        setCanUpdate(permissions.canUpdate);
        setCanDelete(permissions.canDelete);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanUpdate(false);
        setCanDelete(false);
      } finally {
        setIsChecking(false);
        loaderSignal.reset();
      }
    };

    checkPermissions();
  }, [fundingProgram?.id, userAccountSignal.value.userData?.id]);

  const init = useCallback(() => {
    fetchApplication(applicationId);
  }, [applicationId]);

  useEffect(() => {
    init();
  }, [init]);

  if (isChecking && !fundingProgram) {
    return (
      <BreadCrumbWrapper labels={labels}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <p className="text-center">Loading...</p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  if (!isChecking && !canView) {
    return (
      <BreadCrumbWrapper labels={labels}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view this application.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper labels={labels}>
      <SendTechnicalAssistanceModal
        onSendCallback={() => fetchApplication(applicationId)}
      />
      <ApplicationDetail canUpdate={canUpdate} canDelete={canDelete} />
    </BreadCrumbWrapper>
  );
};

export default EdoPortfolioDetail;
